'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import PublicNavbar from '../landing/components/PublicNavbar';
import PublicFooter from '../landing/components/PublicFooter';

const sections = [
  { id: 'acceptance', title: 'Acceptance of Terms' },
  { id: 'platform', title: 'Platform Description' },
  { id: 'eligibility', title: 'Eligibility & Registration' },
  { id: 'senders', title: 'Sender Rules' },
  { id: 'travelers', title: 'Traveler Rules' },
  { id: 'payments', title: 'Payments & Escrow' },
  { id: 'prohibited', title: 'Prohibited Items' },
  { id: 'liability', title: 'Liability & Disputes' },
  { id: 'termination', title: 'Termination' },
  { id: 'governing', title: 'Governing Law' },
];

export default function TermsOfServicePage() {
  const [activeSection, setActiveSection] = useState('acceptance');

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <PublicNavbar />
      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0D2B3E] to-[#0A1628]" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full bg-[#2563EB]/10 blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-[#F59E0B]/10 blur-[100px]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-5">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#2563EB]/15 border border-[#2563EB]/30">
            <svg className="w-4 h-4 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-blue-300 text-sm font-bold uppercase tracking-widest">Terms of Service</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-tight">
            The rules that keep<br />
            <span className="text-[#4DD9CC]">everyone safe.</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            These terms govern your use of MuyuRide. We've written them in plain language so you actually understand what you're agreeing to.
          </p>
          <div className="flex items-center justify-center gap-6 pt-2">
            <div className="text-center">
              <div className="text-2xl font-extrabold text-white">Last Updated</div>
              <div className="text-sm text-slate-400">July 14, 2026</div>
            </div>
            <div className="w-px h-10 bg-white/20" />
            <div className="text-center">
              <div className="text-2xl font-extrabold text-white">Jurisdiction</div>
              <div className="text-sm text-slate-400">Republic of Kenya</div>
            </div>
            <div className="w-px h-10 bg-white/20" />
            <div className="text-center">
              <div className="text-2xl font-extrabold text-white">Version</div>
              <div className="text-sm text-slate-400">3.0</div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#F8FAFC] to-transparent" />
      </section>
      {/* Key highlights */}
      <section className="py-10 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm font-bold text-slate-400 uppercase tracking-widest mb-6">Key Points at a Glance</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: '🔞', title: 'Must Be 18+', desc: 'You must be at least 18 years old to use MuyuRide as a sender or traveler.', color: 'bg-blue-50 border-blue-100' },
              { icon: '🛡️', title: 'Escrow Protection', desc: 'All payments are held in escrow until delivery is confirmed. No pay-before-delivery scams.', color: 'bg-teal-50 border-teal-100' },
              { icon: '⚖️', title: 'Kenyan Law Applies', desc: 'These terms are governed by the laws of the Republic of Kenya. Disputes resolved in Nairobi courts.', color: 'bg-amber-50 border-amber-100' },
              { icon: '🚫', title: 'Zero Tolerance', desc: 'Fraud, prohibited items, and abuse result in immediate permanent account termination.', color: 'bg-red-50 border-red-100' },
            ]?.map((card) => (
              <div key={card?.title} className={`rounded-2xl p-6 border ${card?.color} hover:-translate-y-1 hover:shadow-lg transition-all duration-300`}>
                <div className="text-3xl mb-3">{card?.icon}</div>
                <h3 className="font-extrabold text-[#0F172A] text-sm mb-2">{card?.title}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{card?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Main content */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-12">
            {/* Sticky sidebar */}
            <aside className="hidden lg:block w-64 shrink-0">
              <div className="sticky top-24 rounded-2xl border border-slate-100 bg-[#F8FAFC] p-5">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Contents</p>
                <nav className="space-y-1">
                  {sections?.map((s) => (
                    <a
                      key={s?.id}
                      href={`#${s?.id}`}
                      onClick={() => setActiveSection(s?.id)}
                      className={`block px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                        activeSection === s?.id
                          ? 'bg-[#2563EB] text-white'
                          : 'text-slate-600 hover:text-[#2563EB] hover:bg-blue-50'
                      }`}
                    >
                      {s?.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Terms content */}
            <div className="flex-1 max-w-3xl space-y-14">

              {/* Acceptance */}
              <div id="acceptance" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-xl">✅</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">1. Acceptance of Terms</h2>
                </div>
                <div className="space-y-4 text-slate-600">
                  <p className="leading-relaxed">By accessing or using MuyuRide — including our website at <strong>muyuride.co.ke</strong>, our mobile applications, or any related services — you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use MuyuRide.</p>
                  <p className="leading-relaxed">These Terms constitute a legally binding agreement between you and <strong>MuyuRide Limited</strong>, a company incorporated in Kenya (Registration No. PVT-XXXXXX), with its registered office at Delta Corner, 4th Floor, Westlands, Nairobi.</p>
                  <div className="rounded-xl bg-blue-50 border border-blue-200 p-5">
                    <p className="text-sm font-bold text-blue-800 mb-2">📌 Related Policies</p>
                    <p className="text-sm text-blue-700">These Terms incorporate by reference our <Link href="/privacy-policy" className="underline font-semibold">Privacy Policy</Link>, <Link href="/refund-policy" className="underline font-semibold">Refund Policy</Link>, and any additional guidelines or policies we post on the platform. All of these form part of your agreement with MuyuRide.</p>
                  </div>
                </div>
              </div>

              {/* Platform Description */}
              <div id="platform" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center text-xl">🚀</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">2. Platform Description</h2>
                </div>
                <div className="space-y-4 text-slate-600">
                  <p className="leading-relaxed">MuyuRide is a peer-to-peer marketplace that connects <strong>senders</strong> (individuals who need to send parcels) with <strong>travelers</strong> (individuals who are already traveling between cities and have spare capacity). MuyuRide is a <em>marketplace facilitator</em> — we do not ourselves transport parcels.</p>
                  <div className="rounded-xl bg-amber-50 border border-amber-200 p-5">
                    <p className="text-sm font-bold text-amber-800 mb-2">⚠️ Important Clarification</p>
                    <p className="text-sm text-amber-700">MuyuRide is not a courier company, freight forwarder, or logistics provider. We provide a technology platform that facilitates connections between independent users. The delivery contract is between the sender and the traveler — MuyuRide is not a party to that contract.</p>
                  </div>
                  <p className="leading-relaxed">MuyuRide provides: order posting and matching, traveler verification and trust scoring, escrow payment processing, real-time delivery tracking, messaging between parties, dispute resolution services, and storage listing services.</p>
                </div>
              </div>

              {/* Eligibility */}
              <div id="eligibility" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-xl">👤</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">3. Eligibility & Registration</h2>
                </div>
                <div className="space-y-4 text-slate-600">
                  <p className="leading-relaxed">To use MuyuRide, you must:</p>
                  <ul className="space-y-2">
                    {[
                      'Be at least 18 years of age',
                      'Be a resident of or physically present in Kenya',
                      'Have a valid Kenyan phone number capable of receiving M-Pesa',
                      'Provide accurate, current, and complete registration information',
                      'Not have been previously suspended or banned from MuyuRide',
                      'Have the legal capacity to enter into binding contracts under Kenyan law',
                    ]?.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm">
                        <svg className="w-4 h-4 text-[#0D9488] mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="leading-relaxed"><strong>Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials. You must notify us immediately at <strong>security@muyuride.co.ke</strong> if you suspect unauthorized access to your account. You are liable for all activity that occurs under your account.</p>
                  <p className="leading-relaxed"><strong>One Account Per Person:</strong> You may only maintain one active MuyuRide account. Creating multiple accounts to circumvent suspensions or bans is a violation of these Terms and will result in permanent termination of all accounts.</p>
                </div>
              </div>

              {/* Sender Rules */}
              <div id="senders" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center text-xl">📦</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">4. Sender Rules & Responsibilities</h2>
                </div>
                <div className="space-y-4 text-slate-600">
                  <p className="leading-relaxed">As a sender on MuyuRide, you agree to:</p>
                  <div className="space-y-3">
                    {[
                      { title: 'Accurate Descriptions', desc: 'Provide accurate, complete, and honest descriptions of your parcel — including its contents, weight, dimensions, and declared value. Misrepresentation is grounds for immediate account suspension.' },
                      { title: 'Proper Packaging', desc: 'Package your parcel securely and appropriately for the journey. Fragile items must be clearly marked. You are responsible for damage caused by inadequate packaging.' },
                      { title: 'Legal Contents Only', desc: 'Only send items that are legal to transport in Kenya and that comply with our Prohibited Items list. You are solely responsible for the legality of your parcel\'s contents.' },
                      { title: 'Timely Handover', desc: 'Be available at the agreed pickup time and location. If you miss a confirmed pickup without 2 hours\' notice, you may forfeit your escrow deposit.' },
                      { title: 'Prompt Confirmation', desc: 'Confirm delivery within 24 hours of the traveler marking it as delivered. Failure to confirm within 48 hours will result in automatic release of escrow to the traveler.' },
                      { title: 'Honest Reviews', desc: 'Leave honest, factual reviews of travelers. False negative reviews intended to harm a traveler\'s reputation are a violation of these Terms.' },
                    ]?.map((item) => (
                      <div key={item?.title} className="rounded-xl border border-slate-100 p-5">
                        <h3 className="font-extrabold text-[#0F172A] text-sm mb-2">{item?.title}</h3>
                        <p className="text-sm text-slate-600 leading-relaxed">{item?.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Traveler Rules */}
              <div id="travelers" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-xl">🧳</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">5. Traveler Rules & Responsibilities</h2>
                </div>
                <div className="space-y-4 text-slate-600">
                  <p className="leading-relaxed">As a traveler on MuyuRide, you agree to:</p>
                  <div className="space-y-3">
                    {[
                      { title: 'Identity Verification', desc: 'Complete and maintain up-to-date identity verification, including national ID submission and guarantor registration. Unverified travelers cannot accept orders.' },
                      { title: 'Accurate Trip Information', desc: 'Post only trips you genuinely intend to make. Posting fake trips to collect deposits is fraud and will result in permanent ban and potential criminal referral.' },
                      { title: 'Parcel Inspection', desc: 'Inspect parcels at pickup and document any pre-existing damage with photos. You are responsible for parcels from the moment you accept them until confirmed delivery.' },
                      { title: 'Safe Handling', desc: 'Handle all parcels with reasonable care. Fragile and cold-chain items must be handled according to the sender\'s instructions. Do not open, tamper with, or inspect parcel contents beyond what\'s necessary.' },
                      { title: 'Timely Delivery', desc: 'Deliver parcels within the agreed timeframe. If delays occur, notify the sender immediately via the platform messaging system.' },
                      { title: 'Proof of Delivery', desc: 'Obtain and upload proof of delivery (photo, recipient signature, or OTP confirmation) before marking an order as delivered.' },
                      { title: 'No Subcontracting', desc: 'You may not transfer or subcontract a delivery to another person without the sender\'s explicit written consent. You are personally responsible for every delivery you accept.' },
                    ]?.map((item) => (
                      <div key={item?.title} className="rounded-xl border border-slate-100 p-5">
                        <h3 className="font-extrabold text-[#0F172A] text-sm mb-2">{item?.title}</h3>
                        <p className="text-sm text-slate-600 leading-relaxed">{item?.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Payments */}
              <div id="payments" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center text-xl">💰</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">6. Payments & Escrow</h2>
                </div>
                <div className="space-y-4 text-slate-600">
                  <p className="leading-relaxed">MuyuRide uses an escrow payment system to protect both senders and travelers. Here's how it works:</p>
                  <div className="space-y-3">
                    {[
                      { step: '1', title: 'Sender Pays Upfront', desc: 'When a sender accepts a traveler\'s bid, the agreed delivery fee is immediately charged to the sender\'s M-Pesa or card and held in escrow by MuyuRide.' },
                      { step: '2', title: 'Funds Held Securely', desc: 'Escrowed funds are held in a segregated account. Neither the sender nor the traveler can access these funds during the delivery.' },
                      { step: '3', title: 'Delivery Confirmed', desc: 'When the sender confirms delivery (or 48 hours after the traveler marks it delivered), MuyuRide releases the funds to the traveler.' },
                      { step: '4', title: 'Platform Fee Deducted', desc: 'MuyuRide deducts a service fee of 8–12% (displayed before confirmation) from the escrow before releasing to the traveler. This fee covers platform operations, insurance, and dispute resolution.' },
                    ]?.map((item) => (
                      <div key={item?.step} className="flex gap-4 p-5 rounded-xl border border-slate-100">
                        <div className="w-8 h-8 rounded-lg bg-[#0D9488] text-white text-sm font-extrabold flex items-center justify-center shrink-0">{item?.step}</div>
                        <div>
                          <h3 className="font-extrabold text-[#0F172A] text-sm mb-1">{item?.title}</h3>
                          <p className="text-sm text-slate-600 leading-relaxed">{item?.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-xl bg-teal-50 border border-teal-200 p-5">
                    <p className="text-sm font-bold text-[#0D9488] mb-2">💳 Accepted Payment Methods</p>
                    <p className="text-sm text-slate-600">M-Pesa (Safaricom), Visa, Mastercard (via Stripe). All card payments are processed by Stripe and subject to Stripe's terms. MuyuRide does not store card numbers.</p>
                  </div>
                  <p className="leading-relaxed"><strong>Taxes:</strong> You are responsible for any taxes applicable to your use of MuyuRide, including income tax on earnings as a traveler. MuyuRide will provide annual earnings statements to travelers upon request.</p>
                </div>
              </div>

              {/* Prohibited Items */}
              <div id="prohibited" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center text-xl">🚫</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">7. Prohibited Items</h2>
                </div>
                <div className="rounded-xl bg-red-50 border border-red-200 p-5 mb-5">
                  <p className="text-sm font-bold text-red-700 mb-1">⚠️ Zero Tolerance Policy</p>
                  <p className="text-sm text-red-600">Sending or carrying prohibited items results in immediate permanent account termination, forfeiture of all escrowed funds, and referral to law enforcement where applicable.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { category: 'Illegal Items', items: ['Narcotics and controlled substances', 'Counterfeit goods and currency', 'Stolen property', 'Unlicensed firearms and ammunition', 'Explosives and incendiary devices'] },
                    { category: 'Dangerous Goods', items: ['Flammable liquids and gases', 'Corrosive chemicals', 'Radioactive materials', 'Toxic substances', 'Pressurized containers (aerosols >500ml)'] },
                    { category: 'Restricted Items', items: ['Live animals (without prior written approval)', 'Human remains or body parts', 'Perishable food without cold-chain agreement', 'Cash exceeding KES 50,000', 'Prescription medications (without documentation)'] },
                    { category: 'Platform Violations', items: ['Items intended to defraud the recipient', 'Goods that violate intellectual property rights', 'Items subject to trade sanctions', 'Pornographic or obscene materials', 'Items that violate Kenyan customs regulations'] },
                  ]?.map((cat) => (
                    <div key={cat?.category} className="rounded-xl border border-red-100 bg-white p-5">
                      <h3 className="font-extrabold text-red-700 text-sm mb-3">{cat?.category}</h3>
                      <ul className="space-y-1.5">
                        {cat?.items?.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-xs text-slate-600">
                            <svg className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              {/* Liability */}
              <div id="liability" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-xl">⚖️</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">8. Liability & Disputes</h2>
                </div>
                <div className="space-y-4 text-slate-600">
                  <p className="leading-relaxed"><strong>MuyuRide's Liability:</strong> MuyuRide is a marketplace facilitator. Our maximum liability to you for any claim arising from your use of the platform is limited to the total fees paid by you to MuyuRide in the 3 months preceding the claim. We are not liable for indirect, consequential, or punitive damages.</p>
                  <p className="leading-relaxed"><strong>Traveler Liability:</strong> Travelers are liable for loss or damage to parcels in their custody, up to the declared value of the parcel (maximum KES 50,000 without additional insurance). Travelers with active insurance plans have higher coverage limits as specified in their plan.</p>
                  <p className="leading-relaxed"><strong>Dispute Resolution:</strong> If a dispute arises between a sender and traveler, either party may file a dispute through the platform within 48 hours of the delivery date. MuyuRide will investigate and issue a binding decision within 5 business days. Our dispute resolution process is described in detail in our <Link href="/help-center" className="text-[#0D9488] underline font-semibold">Help Center</Link>.</p>
                  <div className="rounded-xl bg-slate-50 border border-slate-200 p-5">
                    <p className="text-sm font-bold text-slate-700 mb-2">🏛️ Arbitration</p>
                    <p className="text-sm text-slate-600">Any dispute not resolved through our internal process shall be submitted to binding arbitration under the Nairobi Centre for International Arbitration (NCIA) rules. The arbitration shall be conducted in English in Nairobi, Kenya.</p>
                  </div>
                </div>
              </div>

              {/* Termination */}
              <div id="termination" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center text-xl">🔚</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">9. Termination</h2>
                </div>
                <div className="space-y-4 text-slate-600">
                  <p className="leading-relaxed"><strong>By You:</strong> You may close your account at any time through your Profile Settings. Account closure takes effect within 24 hours. Any pending deliveries must be completed or cancelled before closure.</p>
                  <p className="leading-relaxed"><strong>By MuyuRide:</strong> We may suspend or terminate your account immediately, without notice, if you:</p>
                  <ul className="space-y-2">
                    {[
                      'Violate any provision of these Terms',
                      'Engage in fraudulent, deceptive, or illegal activity',
                      'Send or carry prohibited items',
                      'Harass, threaten, or abuse other users or MuyuRide staff',
                      'Create multiple accounts to circumvent a suspension',
                      'Provide false identity information',
                      'Attempt to manipulate the review or trust score system',
                    ]?.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm">
                        <svg className="w-4 h-4 text-red-400 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="leading-relaxed">Upon termination, your right to use MuyuRide ceases immediately. Any escrowed funds will be handled according to our Refund Policy. Data retention follows our Privacy Policy.</p>
                </div>
              </div>

              {/* Governing Law */}
              <div id="governing" className="scroll-mt-24">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center text-xl">🇰🇪</div>
                  <h2 className="text-2xl font-extrabold text-[#0F172A]">10. Governing Law</h2>
                </div>
                <div className="space-y-4 text-slate-600">
                  <p className="leading-relaxed">These Terms are governed by and construed in accordance with the laws of the <strong>Republic of Kenya</strong>, without regard to its conflict of law provisions. You submit to the exclusive jurisdiction of the courts of Kenya for any disputes arising under these Terms.</p>
                  <p className="leading-relaxed">Applicable Kenyan laws include but are not limited to: the Kenya Information and Communications Act, the Consumer Protection Act 2012, the Data Protection Act 2019, the National Payment System Act, and the Kenya Revenue Authority regulations.</p>
                  <div className="rounded-xl bg-[#F8FAFC] border border-slate-100 p-5">
                    <p className="text-sm font-bold text-[#0F172A] mb-2">📬 Legal Notices</p>
                    <p className="text-sm text-slate-600">Legal notices to MuyuRide should be sent to: <strong>legal@muyuride.co.ke</strong> or by post to MuyuRide Limited, Delta Corner, 4th Floor, Westlands, Nairobi, Kenya.</p>
                  </div>
                  <p className="leading-relaxed">If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect. Our failure to enforce any right or provision does not constitute a waiver of that right or provision.</p>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
